import React, { useState } from 'react';
import { Bell, Check, Loader2, X } from 'lucide-react';
import { subscribeToComingSoon } from '../config';

const initial = { firstName: '', lastName: '', email: '', phone: '', gdpr: false };

export default function ProductAlertForm({ product, compact = false, onSuccess }) {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [error, setError] = useState('');

  const onChange = (field) => (e) => {
    const value = field === 'gdpr' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.firstName.trim()) return setError('Indiquez votre prénom.');
    if (!form.lastName.trim()) return setError('Indiquez votre nom.');
    if (!form.email.trim()) return setError('Indiquez votre email.');
    if (!form.gdpr) return setError('Vous devez accepter d\'être contacté à l\'arrivée du produit.');

    setStatus('submitting');
    try {
      await subscribeToComingSoon({
        email: form.email.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        phone: form.phone.trim(),
        gdpr_consent: true,
        product_id: product?.id || '',
        product_name: product?.name || '',
      });
      setStatus('success');
      if (onSuccess) onSuccess();
    } catch (err) {
      setStatus('error');
      setError(err?.message || 'Erreur lors de l\'enregistrement. Réessayez plus tard.');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-sm border border-emerald-500/30 bg-emerald-500/10 p-5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-sm bg-emerald-500/20 flex items-center justify-center">
            <Check className="w-5 h-5 text-emerald-400" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-semibold text-sm uppercase tracking-wider text-white mb-1">
              Vous êtes inscrit
            </div>
            <p className="text-sm text-slate-300">
              Nous vous préviendrons par email dès que <strong className="text-white">{product?.name || 'ce produit'}</strong> sera disponible à la réservation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-sm border border-secondary/30 bg-primary/40 backdrop-blur-sm p-5 space-y-3" data-testid="product-alert-form">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-4 h-4 text-secondary" />
        <span className="font-heading font-semibold text-sm uppercase tracking-wider text-white">
          M'alerter à l'arrivée
        </span>
      </div>
      <p className="text-xs text-slate-300 leading-relaxed">
        Indiquez vos coordonnées : on vous prévient dès que ce PC est disponible à la réservation. Sans engagement.
      </p>

      <div className={`grid gap-2 ${compact ? 'grid-cols-1' : 'grid-cols-2'}`}>
        <input
          type="text"
          placeholder="Prénom"
          value={form.firstName}
          onChange={onChange('firstName')}
          className="bg-primary/60 border border-white/15 rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-secondary"
          disabled={status === 'submitting'}
        />
        <input
          type="text"
          placeholder="Nom"
          value={form.lastName}
          onChange={onChange('lastName')}
          className="bg-primary/60 border border-white/15 rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-secondary"
          disabled={status === 'submitting'}
        />
      </div>
      <input
        type="email"
        placeholder="Email"
        value={form.email}
        onChange={onChange('email')}
        className="w-full bg-primary/60 border border-white/15 rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-secondary"
        disabled={status === 'submitting'}
      />
      <input
        type="tel"
        placeholder="Téléphone (optionnel)"
        value={form.phone}
        onChange={onChange('phone')}
        className="w-full bg-primary/60 border border-white/15 rounded-sm px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-secondary"
        disabled={status === 'submitting'}
      />

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={form.gdpr}
          onChange={onChange('gdpr')}
          className="mt-1 accent-secondary"
          disabled={status === 'submitting'}
        />
        <span className="text-[11px] text-slate-400 leading-snug">
          J'accepte d'être contacté à l'arrivée de ce produit. Mes données sont conservées uniquement pour cette notification (RGPD).
        </span>
      </label>

      {error && (
        <div className="rounded-sm border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full flex items-center justify-center gap-2 bg-secondary hover:bg-secondary/80 disabled:bg-secondary/40 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider py-3 px-6 rounded-sm text-sm transition-all active:scale-95"
        data-testid="product-alert-submit"
      >
        {status === 'submitting' ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Enregistrement...
          </>
        ) : (
          <>
            <Bell className="w-4 h-4" />
            M'alerter à l'arrivée
          </>
        )}
      </button>
    </form>
  );
}
